/**
 * ProteusAppProvider — instantiates the Proteus SDK client once runtime
 * config has loaded and the user has an active access token, then exposes
 * it via the SDK's React provider.
 *
 * Wraps ProteusProvider so child hooks (useProteusPrefs, useLocalStorageMigration,
 * etc.) work without each consumer needing to know about runtime-config
 * + cerberus token plumbing.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ProteusClient } from '@francisquiniai/proteus-sdk'
import { ProteusProvider } from '@francisquiniai/proteus-sdk/react'

import { getAccessToken } from '@/lib/cerberus/tokens'
import { getRuntimeConfig } from '@/lib/runtime-config'
import { useProteusLanguageSync } from './useProteusLanguageSync'

interface Props {
  children: ReactNode
}

export function ProteusAppProvider({ children }: Props) {
  const [baseUrl, setBaseUrl] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    let cancelled = false
    void getRuntimeConfig()
      .then((cfg) => {
        if (cancelled) return
        const url = cfg.api.proteus
        if (!url) {
          // Missing in local dev / preview builds — silent no-op
          setBaseUrl(null)
          return
        }
        setBaseUrl(url.replace(/\/$/, ''))
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const client = useMemo(() => {
    if (!baseUrl) return null
    return new ProteusClient({
      baseUrl,
      getAccessToken: () => getAccessToken(),
    })
  }, [baseUrl])

  if (error) {
    // Hard-fail the child tree only if the runtime config itself errored;
    // missing-key (no Proteus URL) is treated as opt-out.
    throw error
  }

  if (!client) {
    // No Proteus configured — render children without the provider so the
    // app keeps working (consumers can `useContext(ProteusContext)` guarded
    // by try/catch or via a feature flag).
    return <>{children}</>
  }

  return (
    <ProteusProvider client={client}>
      <ProteusSideEffects />
      {children}
    </ProteusProvider>
  )
}

/** Runs hooks that need to live inside the provider tree (language sync +
 * one-time localStorage migration). */
function ProteusSideEffects() {
  useProteusLanguageSync()
  return null
}
